import React, { useMemo } from 'react';
import { TCN, AVATAR_GRADIENTS } from './tokens';

/**
 * TCN Avatar — gradient déterministe (hash du nom) + initiales.
 *
 * Props:
 *  - name: string — used to compute initials + deterministic gradient
 *  - src: optional image URL — overrides initials
 *  - size: px (default 36)
 *  - online: boolean — shows a green status dot
 *  - ring: boolean — adds a subtle borderStrong ring
 */
// Sum-of-charCodes hash — aligned on mockup ui-kit.jsx:294
const hashName = (s = '') => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h += s.charCodeAt(i);
  }
  return h;
};

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({
  name = '',
  src,
  size = 36,
  online = false,
  ring = false,
  className = '',
  style,
}) => {
  const [c1, c2] = useMemo(() => {
    const idx = hashName(name) % AVATAR_GRADIENTS.length;
    return AVATAR_GRADIENTS[idx];
  }, [name]);

  const dotSize = Math.max(8, Math.round(size * 0.25));

  return (
    <div
      className={`relative inline-flex flex-shrink-0 ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-label={name}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          width={size}
          height={size}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            boxShadow: ring ? `0 0 0 1px ${TCN.borderStrong}` : 'none',
          }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Mockup ui-kit.jsx:309 — initials in #fff (white), not black
            color: '#fff',
            fontWeight: 700,
            fontSize: Math.max(10, Math.round(size * 0.38)),
            letterSpacing: '-0.3px',
            fontFamily: TCN.fontSans,
            boxShadow: ring ? `0 0 0 1px ${TCN.borderStrong}` : 'none',
            userSelect: 'none',
          }}
        >
          {initials(name)}
        </div>
      )}
      {online && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            // Mockup ui-kit.jsx:313-316 — bottom/right -1, fixed 10×10, real 2px border (no glow)
            bottom: -1,
            insetInlineEnd: -1,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: TCN.green,
            border: `2px solid ${TCN.bg}`,
          }}
        />
      )}
    </div>
  );
};

export default Avatar;
